import {
  assigneeOptions,
  bookedDay,
  inspectionAssignees,
  jobLocation,
  crewLabel,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { navigate, useAsync } from "../../lib/hooks";
import { fetchScheduledWork } from "../../lib/queries";
import { startOfWeek } from "../../lib/format";
import { useSession } from "../../session";
import type { AppData } from "../shell/Shell";

/**
 * Week view showing scheduled inspections and every crew's booked day.
 *
 * It used to show one "roof date" per job, which was wrong twice over: a
 * gutters-only job never had one, and a roof-plus-siding job hid the siding
 * day behind the roof day. The days now come from the work orders, so each
 * crew's visit appears on the day it is actually booked for.
 */
export function Calendar({ data }: { data: AppData }) {
  const { people, crews } = useSession();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const assignees = assigneeOptions(people, crews);

  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    return day;
  });

  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);

  const inspections = data.jobs.filter(
    (job) => job.inspection_at && job.stage === "inspection_scheduled",
  );
  const scheduled = useAsync(fetchScheduledWork, []);
  const jobById = new Map(data.jobs.map((job) => [job.id, job]));

  const shift = (weeks: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + weeks * 7);
    setWeekStart(next);
  };

  return (
    <>
      <div className="listhead">
        <h2>{t("Calendar")}</h2>
        <div className="lh-right">
          <button className="calnav" onClick={() => shift(-1)}>
            ‹
          </button>
          <button
            className="calnav"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
          >
            {t("Today")}
          </button>
          <button className="calnav" onClick={() => shift(1)}>
            ›
          </button>
          <span className="count">
            {weekStart.toLocaleDateString([], {
              month: "short",
              day: "numeric",
            })}{" "}
            – {end.toLocaleDateString([], { month: "short", day: "numeric" })}
          </span>
        </div>
      </div>

      <div className="calgrid">
        {days.map((day) => {
          const key = day.toDateString();
          const events: { sort: number; node: React.ReactNode }[] = [];

          for (const job of inspections) {
            if (new Date(job.inspection_at!).toDateString() !== key) continue;
            const at = new Date(job.inspection_at!);
            events.push({
              sort: at.getTime(),
              node: (
                <div
                  key={`i${job.id}`}
                  className="ev insp"
                  onClick={() => navigate(`job=${job.id}`)}
                >
                  <div className="evt">
                    {at.toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    · {t("Inspection")}
                    {/* Who is going, so this reads as somebody's calendar
                        rather than a list of appointments in the abstract. */}
                    {inspectionAssignees(job, assignees).length > 0 && (
                      <>
                        {" · "}
                        {inspectionAssignees(job, assignees)
                          .map((a) => a.name)
                          .join(" & ")}
                      </>
                    )}
                  </div>
                  <div className="evn">{job.contact?.full_name ?? "—"}</div>
                  <div className="eva">{jobLocation(job).address ?? ""}</div>
                </div>
              ),
            });
          }

          for (const order of scheduled.data ?? []) {
            /*
             * The day the crew works, not the day somebody ticked "Schedule".
             *
             * `scheduled_at` is a checklist stamp, so an order ticked on Monday
             * for the following Tuesday was drawn on Monday — a calendar that
             * quietly told the office the wrong day. `start_at` is the box that
             * asks when the crew starts and is the only field that answers it.
             *
             * An order scheduled with no start date yet has no day to be drawn
             * on. It is not lost — the job still lists it as scheduled — it
             * just is not a calendar entry until somebody says when.
             */
            const day = bookedDay(order);
            if (day === null) continue;
            const at = new Date(day);
            if (at.toDateString() !== key) continue;
            const job = jobById.get(order.job_id);
            if (!job) continue;
            events.push({
              sort: at.getTime(),
              node: (
                <div
                  key={`w${order.id}`}
                  className={`ev job${order.completed_at ? " done" : ""}`}
                  onClick={() => navigate(`job=${job.id}`)}
                >
                  <div className="evt">
                    {at.toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    · {order.trades || t("Work")}
                    {crewLabel(order.crew) ? ` · ${crewLabel(order.crew)}` : ""}
                  </div>
                  <div className="evn">{job.contact?.full_name ?? "—"}</div>
                  <div className="eva">{jobLocation(job).address ?? ""}</div>
                </div>
              ),
            });
          }

          events.sort((a, b) => a.sort - b.sort);
          const isToday = key === new Date().toDateString();

          return (
            <div className={`calday${isToday ? " today" : ""}`} key={key}>
              <div className="caldate">
                {day.toLocaleDateString([], { weekday: "short" })}{" "}
                <b>{day.getDate()}</b>
              </div>
              {events.length ? (
                events.map((e) => e.node)
              ) : (
                <div className="calnone">—</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="callegend">
        <span>
          <span className="dotb" />
          {t("Inspection")}
        </span>
        <span>
          <span className="dotg" />
          {t("Ongoing job")}
        </span>
      </div>
    </>
  );
}
